import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { useState } from "react";

const STEPS = [
  { n: "01", t: "Select Your Gift Card", d: "Choose your gift card brand, enter the card value, and view the current estimated payout using our live rate calculator before you begin your submission.", icon: "/jumbo/images/chat-2-1.svg", img: "/bum/step1.png" },
  { n: "02", t: "Complete Your Submission", d: "Follow the guided submission process and provide the information required to review your eligible gift card securely.", icon: "/jumbo/images/shield-1.svg", img: "/bum/step2.png" },
  { n: "03", t: "Verification & Review", d: "Our team reviews each submission to verify eligibility and complete the approval process before payment is issued.", icon: "/jumbo/images/customizable-icon.svg", img: "/bum/step3.png" },
  { n: "04", t: "Receive Your Payout", d: "Once your gift card has been successfully verified and approved, your payout is sent using the payment method you selected during submission. You can track your transaction status at every stage.", icon: "/jumbo/images/browser-icon.svg", img: "/bum/step4.png" },
];

const PROMISES = [
  "Sell unused gift cards through a secure exchange",
  "No signup required — submit as a guest in one flow",
  "Every submission is reviewed by a real person",
  "Track your transaction status with your SPARK reference",
  "Multiple payout options: PayPal, Cash App, Zelle",
  "Encrypted uploads with automatic purge after payout",
];

export default function HowItWorksPage() {
  const [tab, setTab] = useState(0);
  const s = STEPS[tab];

  return (
    <>
      <SEO title="How to sell gift cards online — GC SPARK" description="Learn how to sell your unused gift cards online with GC SPARK: use our live rate calculator, complete a secure submission, and receive your payout after verification and approval." />

      {/* Compact search-style hero */}
      <section className="search-home-hero-section" data-testid="how-hero">
        <div className="main-container w-container">
          <div className="home-hero-container">
            <div className="home-hero-text-container" style={{ textAlign: "center", margin: "0 auto" }}>
              <div className="home-hero-heading">
                <h1 className="h1 _700-bold">How to sell your gift cards in <span style={{ color: "#346af8" }}>four steps</span></h1>
              </div>
              <div className="home-hero-para">
                <p>See how our secure gift card exchange works — from checking the live rate to receiving your payout after verification and approval.</p>
              </div>
              <div style={{ marginTop: 26, display: "inline-block" }}>
                <Link to="/submit" className="primary-button w-button" data-testid="how-cta-submit">Sell a Gift Card</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature-tabs style walkthrough */}
      <section className="feature-section" data-testid="how-steps">
        <div className="main-container w-container">
          <div className="feature-container">
            <div className="feature-heading">
              <h2 className="h2 _700-bold">A simple, secure process from start to payout</h2>
            </div>
            <div className="feature-row-wrapper">
              <div className="feature-row-1">
                <div className="tabs">
                  <div className="tab-content">
                    <div className="tab-panel">
                      <div className="tab-wrapper">
                        <figure className="how-step-figure" style={{
                          width: "100%",
                          margin: 0,
                          padding: 30,
                          borderRadius: 28,
                          background: "linear-gradient(135deg, #f4f6ff 0%, #eef2ff 100%)",
                          boxShadow: "0 30px 60px -30px rgba(41,45,85,.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: 460,
                          position: "relative",
                          overflow: "hidden",
                        }}>
                          {/* decorative accents */}
                          <span style={{ position: "absolute", top: 24, right: 24, padding: "6px 14px", borderRadius: 90, background: "#fff", color: "#346af8", fontSize: 11, fontWeight: 800, letterSpacing: ".14em", boxShadow: "0 6px 14px -6px rgba(52,106,248,.35)" }}>STEP {s.n}</span>
                          <span style={{ position: "absolute", bottom: -60, left: -60, width: 180, height: 180, borderRadius: "50%", background: "rgba(52,106,248,.06)" }} />
                          <span style={{ position: "absolute", top: -40, left: 40, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,201,71,.15)" }} />
                          <img
                            src={s.img}
                            loading="lazy"
                            alt={s.t}
                            style={{
                              width: "100%",
                              maxWidth: 560,
                              height: "auto",
                              objectFit: "contain",
                              display: "block",
                              position: "relative",
                              zIndex: 1,
                              filter: "drop-shadow(0 20px 40px rgba(41,45,85,.15))",
                            }}
                          />
                        </figure>
                      </div>
                    </div>
                  </div>
                  <div className="tabs-menu">
                    {STEPS.map((step, i) => (
                      <a key={step.n} href="#steps"
                        className={`tab-link w-inline-block${tab === i ? " w--current" : ""}`}
                        onClick={(e) => { e.preventDefault(); setTab(i); }}
                        data-testid={`how-step-${i + 1}`}
                        style={tab === i ? { borderColor: "#346af8" } : undefined}>
                        <div className="tab-link-image-wrapper">
                          <img src={step.icon} loading="lazy" alt="" className="tab-link-image" />
                        </div>
                        <div className="tab-link-text">
                          <div className="h5 _700-bold">{step.n}. {step.t}</div>
                        </div>
                        <div className="tab-para">
                          <p className="main-body-text _500-medium">{step.d}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why sellers stick — checklist band */}
      <section className="feature-section" style={{ paddingTop: 0 }} data-testid="how-promises">
        <div className="main-container w-container">
          <div className="feature-row-3">
            <figure className="why-us-left-image-wrapper">
              <img src="/jumbo/images/receive-payment.svg" loading="lazy" alt="Payouts" className="why-us-left-image" />
            </figure>
            <div className="why-us-text-container right-side-text">
              <div className="feature-heading left-aligned">
                <h3 className="h2 _700-bold">Six things every seller gets from SPARK</h3>
              </div>
              <div className="why-us-para">
                <p className="main-body-text-variant-1 _500-medium">
                  We wrote these down so you can hold us to them. Everything you see here applies to the very first card you submit.
                </p>
              </div>
              <div className="feature-list-div">
                <div className="update-list-wrappper">
                  {PROMISES.map((c) => (
                    <div className="why-us-list-row" key={c}>
                      <figure className="check-icon-wrapper">
                        <img src="/jumbo/images/checkamrk.svg" loading="lazy" alt="" className="check-icon" />
                      </figure>
                      <div>{c}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="feature-btn-wrapper">
                <Link to="/rates" className="btn w-inline-block" data-testid="how-cta-rates">
                  <div className="btn-text">Check today's rates</div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="boss-section" data-testid="how-cta">
        <div className="boss-container">
          <div className="boss-cta-band">
            <h2>Ready when you are</h2>
            <p>Submitting a card takes about three minutes. You can sell as a guest — no signup required.</p>
            <Link to="/submit" className="boss-btn" data-testid="how-cta-bottom">Sell my card</Link>
          </div>
        </div>
      </section>
    </>
  );
}
